import { useState } from 'react';
import {
  useExportMenu,
  useExportPosData,
  useRefundOrderItem,
  useOrders,
  useSendMenu,
  useRevokeOrderItemRefund,
  useExportOrders,
  useUpdateOrderItem,
  useOrderFilterOptions,
} from '../utils/api.hook';
import { OrdersComponent } from './orders.component';
import dayjs, { Dayjs } from 'dayjs';
import { FilterValue } from 'antd/es/table/interface';
import { usePagination } from '../shared/usePagination';
import { QP, useQueryParams } from '../shared/useQueryParams';

type DateRange = { startTime: Dayjs | undefined; endTime: Dayjs | undefined };
type OrderState = '已確認' | '未確認' | '取消訂單' | '取消訂單-退貨' | '結案';

const ordersQuerySchema = {
  keyword: QP.string('keyword'),
  address: QP.stringArray('address'),
  email: QP.stringArray('email'),
  pickupNo: QP.stringArray('pickupNo'),
  providerNames: QP.stringArray('providerNames'),
  state: QP.stringArray('state'),
  timeRange: QP.dayjsRange('startTime', 'endTime'),
  redeemRange: QP.dayjsRange('redeemStart', 'redeemEnd'),
};

// 取貨日期未指定時，預設撈取當月區間（OrderItem.redeemTime 有索引，避免全表掃描）
const currentMonthRange = (): DateRange => ({
  startTime: dayjs().startOf('month'),
  endTime: dayjs().endOf('month'),
});

const toRedeemTimeRange = (range: DateRange): string[] | undefined =>
  range.startTime && range.endTime
    ? [range.startTime.toISOString(), range.endTime.toISOString()]
    : undefined;

const toStringArray = (
  value: FilterValue | null | undefined
): string[] | undefined => (value && value.length ? value.map(String) : undefined);

export function OrdersContainer() {
  const pagination = usePagination({ defaultPageSize: 10, syncUrl: true });
  const { values, setValues } = useQueryParams(ordersQuerySchema);
  const { keyword, timeRange, address, email, pickupNo, providerNames, state } =
    values;
  const filters = { address, email, pickupNo, providerNames, state };
  const redeemRange =
    values.redeemRange.startTime || values.redeemRange.endTime
      ? values.redeemRange
      : currentMonthRange();
  const redeemTimeRange = toRedeemTimeRange(redeemRange);
  const setKeyword = (value: string) => setValues({ keyword: value });
  const setTimeRange = (range: DateRange) => setValues({ timeRange: range });
  const setRedeemRange = (range: DateRange) =>
    setValues({ redeemRange: range });
  const setFilters = (next: Record<string, FilterValue | null>) =>
    setValues({
      address: toStringArray(next.address),
      email: toStringArray(next.email),
      pickupNo: toStringArray(next.pickupNo),
      providerNames: toStringArray(next.providerNames),
      state: toStringArray(next.state),
    });
  const [exportDate, setExportDate] = useState<Dayjs | undefined>(undefined);
  const [exportLocationIds, setExportLocationIds] = useState<string[]>([]);
  const [exportLayoutType, setExportLayoutType] = useState<string>('');
  const { mutateAsync: sendMenu } = useSendMenu();
  const { refetch: exportMenu, isLoading: isExporting } = useExportMenu({
    date: exportDate,
    locationIds: exportLocationIds,
  });
  const { refetch: exportPosData, isLoading: isExportingPosData } =
    useExportPosData({
      date: exportDate,
      locationIds: exportLocationIds,
      layoutType: exportLayoutType,
    });
  const {
    data: { orders, total } = {},
    isFetching,
    isLoading,
  } = useOrders({
    ...timeRange,
    ...filters,
    state: state as OrderState[] | undefined,
    redeemTimeRange,
    keyword,
    skip: pagination.skip,
    take: pagination.take,
  });
  const {
    data: {
      addressFilters,
      providerNameFilters,
      emailFilters,
      pickupNoFilters,
    } = {},
  } = useOrderFilterOptions();
  const { mutateAsync: refundOrderItem, isLoading: isRefunding } =
    useRefundOrderItem();
  const { mutateAsync: revokeOrderItemRefund, isLoading: isRevertingRefund } =
    useRevokeOrderItemRefund();
  const exportParams = {
    ...timeRange,
    ...filters,
    state: state as OrderState[] | undefined,
    redeemTimeRange,
    keyword,
  };
  const {
    isLoading: isOrderExporting,
    isFetching: isOrderFetching,
    refetch: refetchExportOrders,
  } = useExportOrders(exportParams);
  const { mutateAsync: updateOrderItem, isLoading: isUpdatingOrderItem } =
    useUpdateOrderItem();

  const exportOrders = async () => {
    await refetchExportOrders();
  };

  return (
    <OrdersComponent
      addressFilters={addressFilters}
      emailFilters={emailFilters}
      pickupNoFilters={pickupNoFilters}
      providerNameFilters={providerNameFilters}
      exportMenu={exportMenu}
      exportOrders={exportOrders}
      exportPosData={exportPosData}
      orders={orders}
      refundOrderItem={refundOrderItem}
      revokeRefund={revokeOrderItemRefund}
      sendMenu={sendMenu}
      setExportDate={setExportDate}
      setExportLocationIds={setExportLocationIds}
      setExportLayoutType={setExportLayoutType}
      setKeyword={setKeyword}
      setFilters={setFilters}
      setTimeRange={setTimeRange}
      keyword={keyword}
      filters={filters}
      timeRange={timeRange}
      redeemRange={redeemRange}
      setRedeemRange={setRedeemRange}
      updateOrderItem={updateOrderItem}
      pagination={{
        ...pagination,
        total,
      }}
      isLoading={
        isLoading ||
        isExporting ||
        isExportingPosData ||
        isRefunding ||
        isRevertingRefund ||
        isFetching ||
        isOrderExporting ||
        isOrderFetching ||
        isUpdatingOrderItem
      }
    />
  );
}
